class CreateAiModel {
	constructor(
		public readonly accountId: number,
		public readonly providerId: number,
		public readonly identifier: string,
		public readonly label: string,
		public readonly usages: string[],
		// `null` means "no override": the provider's own `apiVersion` applies. The server refuses a
		// non-null value when the model's provider is not `azure_openai`.
		public readonly apiVersion: string | null,
		// `null` means "no family": only meaningful for audio models. Always sent explicitly, `null`
		// included, so the server can distinguish "no family" from "leave the family untouched".
		public readonly family: string | null = null,
		// Provider-specific settings. Write-only: the server never echoes it back, so a `null` here
		// means "nothing to send", not "clear the settings" — see `toJSON()` below.
		public readonly providerOptions: Record<string, unknown> | null = null,
		// `null` for a text model. Always sent explicitly, `null` included.
		public readonly deliveryMode: string | null = null,
		public readonly transcriptionChunking: boolean = true,
		// `null` means "the server's own value applies". Always sent explicitly, `null` included.
		public readonly transcriptionChunkMaxSeconds: number | null = null,
		public readonly transcriptionConcurrency: number | null = null,
		// Prices in euros, `null` meaning "not set". Always sent explicitly, `null` included: the form is
		// filled from the stored values, so what it submits is the whole intended state.
		public readonly inputPricePerMillionTokens: number | null = null,
		public readonly outputPricePerMillionTokens: number | null = null,
		public readonly cachedInputPricePerMillionTokens: number | null = null,
		public readonly cacheWritePricePerMillionTokens: number | null = null,
		public readonly pricePerAudioMinute: number | null = null,
		public readonly billedPerStartedMinute: boolean = false,
	) {
		Object.freeze(this);
	}

	// `accountId` is the route identifier: never echoed in the request body.
	toJSON(): Record<string, unknown> {
		const body: Record<string, unknown> = {
			providerId: this.providerId,
			identifier: this.identifier,
			label: this.label,
			usages: this.usages,
			apiVersion: this.apiVersion,
			family: this.family,
			deliveryMode: this.deliveryMode,
			transcriptionChunking: this.transcriptionChunking,
			transcriptionChunkMaxSeconds: this.transcriptionChunkMaxSeconds,
			transcriptionConcurrency: this.transcriptionConcurrency,
			inputPricePerMillionTokens: this.inputPricePerMillionTokens,
			outputPricePerMillionTokens: this.outputPricePerMillionTokens,
			cachedInputPricePerMillionTokens: this.cachedInputPricePerMillionTokens,
			cacheWritePricePerMillionTokens: this.cacheWritePricePerMillionTokens,
			pricePerAudioMinute: this.pricePerAudioMinute,
			billedPerStartedMinute: this.billedPerStartedMinute,
		};

		// `providerOptions` is write-only and never comes back from the server, so the form cannot
		// re-emit it. Omitting the key when it is `null` keeps it out of the request entirely, rather
		// than sending an explicit erase the caller never asked for.
		if (this.providerOptions !== null) {
			body.providerOptions = this.providerOptions;
		}

		return body;
	}
}

export default CreateAiModel;
